import { addSeconds, endOfDay, format, setMilliseconds, startOfDay, subDays } from "date-fns";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import { DateFormat } from "constants/date";
import { GlobalModal } from "constants/globalModal";
import { ReportType } from "constants/reports/report-type";
import { sliceSortedObjectKeys } from "helpers/object";
import { TicketStatus } from "interfaces/entities/ticket";
import { ApiManager } from "services/api";
import { showGlobalModal } from "store/actions";
import { IActionWithPayload } from "store/helper";
import { sumValueFromPeriod } from "store/views/reports/helpers";

import { RequestAction } from "../actions";

import {
  CreateReportActionNames,
  DeleteScheduledReportActionNames,
  EditScheduledReportActionNames,
  REPORT,
  ReportActions,
} from "./actions";
import { ICreateReportPayload, IDeleteReportPayload, IEditReportPayload, IFetchReportPayload } from "./interfaces";
import { fetchAuditLog } from "./sagas/fetch-audit-log";
import { fetchHandlingTimeByStatus } from "./sagas/fetch-handling-time-by-status";
import { heatmapDataFetching } from "./sagas/fetch-heatmap";
import { fetchNewTickets } from "./sagas/fetch-new-tickets";
import { fetchSolvedClosedTickets } from "./sagas/fetch-solved-tickets";
import { fetchTicketFirstResponseTime } from "./sagas/fetch-ticket-first-response-time";
import { fetchTicketRating } from "./sagas/fetch-ticket-rating";
import { fetchTicketResolutionTime } from "./sagas/fetch-ticket-resolution-time";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* fetchLast7Days(action: any): SagaIterator {
  const { source } = action.payload;
  const last14DaysParams = {
    dateFrom: format(startOfDay(subDays(new Date(), 13)), DateFormat.ISO),
    dateTo: format(setMilliseconds(addSeconds(endOfDay(new Date()), 1), 0), DateFormat.ISO),
  };

  const responses = yield all([
    // @ts-expect-error TS(2769)
    call(ApiManager.reportApi.fetchNewTickets, { ...last14DaysParams }),
    // @ts-expect-error TS(2769)
    call(ApiManager.reportApi.fetchTicketsSatisfaction, { ...last14DaysParams }),
    // @ts-expect-error TS(2769)
    call(ApiManager.reportApi.fetchTicketsWithStatus, { ...last14DaysParams, status: TicketStatus.Solved }),
    // @ts-expect-error TS(2769)
    call(ApiManager.reportApi.fetchTicketsWithStatus, { ...last14DaysParams, status: TicketStatus.Closed }),
    // @ts-expect-error TS(2769)
    call(ApiManager.reportApi.fetchTicketsFirstResponseTime, { ...last14DaysParams }),
  ]);

  const [
    newTicketsResponse,
    ticketsSatisfactionResponse,
    solvedTicketsResponse,
    closedTicketsResponse,
    firstResponseTimeResponse,
  ] = responses;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (responses.every((response: any) => response.result)) {
    let newTicketsLastWeek = null;
    let newTicketsPreviousWeek = null;
    if (newTicketsResponse && newTicketsResponse.result) {
      newTicketsLastWeek = sliceSortedObjectKeys(newTicketsResponse.result, 7, 14);
      newTicketsPreviousWeek = sliceSortedObjectKeys(newTicketsResponse.result, 0, 7);
    }

    let ticketsSatisfactionLastWeek = null;
    let ticketsSatisfactionPreviousWeek = null;
    if (ticketsSatisfactionResponse && ticketsSatisfactionResponse.result) {
      ticketsSatisfactionLastWeek = sliceSortedObjectKeys(ticketsSatisfactionResponse.result, 7, 14);
      ticketsSatisfactionPreviousWeek = sliceSortedObjectKeys(ticketsSatisfactionResponse.result, 0, 7);
    }

    const solvedTicketsResponseResult = sliceSortedObjectKeys(solvedTicketsResponse.result, 7, 14);
    const solvedTickets = sumValueFromPeriod(solvedTicketsResponseResult, "count");

    const closedTicketsResponseResult = sliceSortedObjectKeys(closedTicketsResponse.result, 7, 14);
    const closedTickets = sumValueFromPeriod(closedTicketsResponseResult, "count");

    const firstResponseTimeResult = sliceSortedObjectKeys(firstResponseTimeResponse.result, 7, 14);
    const firstResponseTimeTickets = sumValueFromPeriod(firstResponseTimeResult, "count");
    const firstResponseTimeTimes = sumValueFromPeriod(
      firstResponseTimeResult,
      "sumSecondsToAssignment",
      "sumSecondsToResponse"
    );
    const averageFirstResponseTime =
      firstResponseTimeTickets > 0 ? firstResponseTimeTimes / firstResponseTimeTickets : 0;

    const data = {
      newTickets: newTicketsLastWeek,
      newTicketsBefore: newTicketsPreviousWeek,
      ticketsSatisfaction: ticketsSatisfactionLastWeek,
      ticketsSatisfactionBefore: ticketsSatisfactionPreviousWeek,
      solvedTickets,
      closedTickets,
      averageFirstResponseTime,
    };

    yield put(
      ReportActions.fetchSuccess({
        data,
        source,
        reportType: ReportType.Last7days,
      })
    );
  } else {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errors = responses.map((request: any) => request.error && request.error.error);

    yield put(ReportActions.fetchFailure({ errors, reportType: ReportType.Last7days }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* fetchScheduledReports(action: IActionWithPayload<string, IFetchReportPayload>): any {
  const {
    payload: { reportType, source },
  } = action;
  const data = yield call(ApiManager.exportApi.fetchScheduledReports);

  if (data.result) {
    yield put(
      ReportActions.fetchSuccess({
        data,
        source,
        reportType,
      })
    );
  } else {
    yield put(ReportActions.fetchFailure({ error: data.error, reportType: ReportType.ScheduledReports }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* createReport(action: IActionWithPayload<string, ICreateReportPayload>): any {
  const {
    payload: { data, source },
  } = action;

  const { email, format: editFormat, encoding, type, period, dateFrom, dateTo } = data;

  const reportData = period
    ? yield call(ApiManager.exportApi.createReportRecurring, {
        email,
        format: editFormat,
        encoding,
        type,
        period,
      })
    : yield call(ApiManager.exportApi.createReportOneTime, {
        email,
        format: editFormat,
        encoding,
        type,
        dateFrom,
        dateTo,
      });

  if (reportData.result && !reportData.result.error) {
    if (period) {
      yield put(
        ReportActions.createReportSuccess({
          data: reportData.result,
          source,
        })
      );
    } else {
      yield put(
        ReportActions.createReportSuccess({
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'IReportCrea... Remove this comment to see the full error message
          data: null,
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'ViewActionS... Remove this comment to see the full error message
          source: null,
        })
      );
      yield put(
        showGlobalModal({
          modal: GlobalModal.OneTimeReportSended,
          payload: {},
        })
      );
    }
  } else {
    yield put(ReportActions.createReportFailure({ error: reportData.error, data }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* editScheduledReport(action: IActionWithPayload<string, IEditReportPayload>): any {
  const {
    payload: { data, source },
  } = action;

  const { email, format: editFormat, encoding, type, period, typeCurrent, periodCurrent } = data;

  const reportData = yield call(ApiManager.exportApi.editScheduledReport, {
    email,
    format: editFormat,
    encoding,
    type,
    period,
    typeCurrent,
    periodCurrent,
  });

  if (reportData.result && !reportData.result.error) {
    reportData.result.periodCurrent = periodCurrent;
    reportData.result.typeCurrent = typeCurrent;

    yield put(
      ReportActions.editScheduledReportSuccess({
        data: reportData.result,
        source,
      })
    );
  } else {
    yield put(ReportActions.editScheduledReportFailure({ error: reportData.error, data }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* deleteScheduledReport(action: IActionWithPayload<string, IDeleteReportPayload>): any {
  const {
    payload: {
      source,
      data: { id },
    },
  } = action;
  const [type, recurs] = id.split("-");

  const data = yield call(ApiManager.exportApi.deleteScheduledReport, { type, recurs });

  if (data.result) {
    yield put(
      ReportActions.deleteScheduledReportSuccess({
        data: { id },
        source,
      })
    );
  } else {
    yield put(ReportActions.deleteScheduledReportFailure({ error: data.error, data: { id } }));
  }
}

const reportCallMap = {
  [ReportType.Last7days]: fetchLast7Days,
  [ReportType.TicketRatings]: fetchTicketRating,
  [ReportType.ScheduledReports]: fetchScheduledReports,
  [ReportType.SolvedAndClosed]: fetchSolvedClosedTickets,
  [ReportType.TicketFirstResponseTime]: fetchTicketFirstResponseTime,
  [ReportType.NewTickets]: fetchNewTickets,
  [ReportType.NewTicketsHeatmap]: heatmapDataFetching,
  [ReportType.TicketResolutionTime]: fetchTicketResolutionTime,
  [ReportType.AuditLog]: fetchAuditLog,
  [ReportType.HandlingTimeByStatus]: fetchHandlingTimeByStatus,
};

export default function* reportsSaga(): SagaIterator {
  yield all([
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(CreateReportActionNames[RequestAction.REQUEST], createReport),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(EditScheduledReportActionNames[RequestAction.REQUEST], editScheduledReport),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(DeleteScheduledReportActionNames[RequestAction.REQUEST], deleteScheduledReport),
    ...Object.keys(reportCallMap).map(reportName =>
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      takeLatest(REPORT[reportName][RequestAction.REQUEST], reportCallMap[reportName])
    ),
  ]);
}
